import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Building2, Calendar, Loader2 } from 'lucide-react';
import { LeadManagementRows } from './LeadManagementRows';
import { LeadAutomazioniInCorso } from './LeadAutomazioniInCorso';
import { LeadClassificationRows } from './LeadClassificationRows';
import { LeadInfoRows } from './LeadInfoRows';
import { LeadCommentsSection } from './LeadCommentsSection';
import { LeadPeopleSection } from './LeadPeopleSection';
import { LeadNewsletterRow, LeadPipelineRow, LeadReferrerRow } from './LeadSingleRows';
import { LeadDetailsHeader } from './LeadDetailsHeader';
import LeadNotesSection from './LeadNotesSection';
import type { ComposerDrawerPayload, ComposerMode } from '../shared/timeline/InlineComposer';
import { CustomFieldsSection } from '../clients/CustomFieldsSection';
import { ClientSidePanel } from '../clients/ClientSidePanel';
import { ClientAvatar } from '../clients/ClientAvatar';
import { ClientOriginCard } from '../marketing/ClientOriginCard';
import { PrivacyConsentCard } from './PrivacyConsentCard';
import { EnrichmentLeadCard } from '../enrichment/EnrichmentLeadCard';
import { EnrichmentProposalsSection } from '../enrichment/EnrichmentProposalsSection';
import { PanelSection } from '../shared/property-rows';
import { BETA_VOICE_AGENTS_ORG_IDS, canAccessBetaFeature } from '../../lib/config/betaOrgs';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useCurrentUser } from '../../lib/hooks/useCurrentUser';
import { PanelErrorBoundary } from './PanelErrorBoundary';
import { supabase } from '../../lib/supabase';
import { hasAlfaDocsLink } from '../../lib/services/alfadocsLinks';
// Task 3 "Partner drawer unificato": sezione property rows partner-specifiche.
// Task 1 "Tab Info stile Notion": tab Info montato via PartnerInfoTab (contenitore a
// sezioni toggle), che a sua volta monta Commissioni/Portal.
import { PartnerInfoExtras } from '../partners/PartnerInfoExtras';
import { PartnerAgnosticInfoRows } from '../partners/PartnerAgnosticInfoRows';
import { PartnerInfoTab } from '../partners/info/PartnerInfoTab';
// Task 4 "Partner drawer unificato": slot fase pipeline al posto di LeadStatusManager.
import { PartnerStageSlot } from '../partners/PartnerStageSlot';
import { useOpenWhatsAppChat } from '../whatsapp-overlay/useOpenWhatsAppChat';
import type { ExternalPartner } from '../../lib/types';
import type { PartnerExtras } from '../../pages/partners/usePartnerEntity';

// Lazy load per non caricare il bundle AlfaDocs se non serve
const AlfaDocsClientTab = lazy(() => import('../alfadocs/AlfaDocsClientTab').then(m => ({ default: m.AlfaDocsClientTab })));

interface LeadDesktopLayoutProps {
  selectedClient: any;
  // Task 2 "Partner drawer unificato": tipo esteso dell'entità mostrata (include
  // 'partner'). Opzionale, non consumato qui — branch di rendering in Task 3.
  entityType?: 'lead' | 'client' | 'partner';
  isLead: boolean;
  showExpandedDetails: boolean;
  setShowExpandedDetails: (show: boolean) => void;
  shouldHideTags: boolean;
  leadStagesFromDb: any[];
  clientStateOptions: any[];
  availableUsers: any[];
  notes: any[];
  activities: any[];
  notesLoading: boolean;
  t: any;
  detailsT: any;
  formatDate: (date: string) => string;
  handleLeadUpdate: (id: string, updates: any) => Promise<boolean>;
  handleEditLead: () => void;
  // Task 4 "Partner drawer unificato": callback dedicata alla modifica partner,
  // inoltrata a LeadDetailsHeader (collegata a PartnerForm dal Task 5).
  onEdit?: () => void;
  handleCopy: (value: string, label: string) => void;
  handleAddNote: () => void;
  handleAddTask: () => void;
  handleEditNote: (noteId: string, content: string) => Promise<void>;
  handleDeleteNote: (noteId: string) => Promise<void>;
  handleDeleteTask: (taskId: string, opts?: { skipGoogleSync?: boolean }) => Promise<void>;
  handleEditTask: (taskId: string, updates: any) => Promise<void>;
  handleUpdateActivities?: (activities: any[]) => void;
  /** Wiring del composer inline della cronologia (vedi `LeadNotesSection`). */
  composer?: {
    onSaved: () => void | Promise<void>;
    onOpenDrawer: (mode: ComposerMode, payload: ComposerDrawerPayload) => void;
  };
  onAddPartner: () => void;
  showSkeleton: boolean;
  // Task 3 "Partner drawer unificato": entità partner grezza (richiesta dai manager
  // Commissioni/Portal nella colonna destra) e campi extra per PartnerInfoExtras.
  // Presenti solo quando entityType === 'partner' (undefined altrimenti).
  partner?: ExternalPartner | null;
  partnerExtras?: PartnerExtras | null;
  isModal?: boolean;
  // Props for header (when isModal=true)
  showActionsMenu?: boolean;
  setShowActionsMenu?: (show: boolean) => void;
  isConverting?: boolean;
  onCreateDeal?: () => void;
  onConvertToClient?: () => void;
  onClose?: () => void;
  pipelineContext?: string; // Pipeline corrente per filtrare note/attività
}

// Notion-style divider
const Divider = () => (
  <div className="border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />
);

export function LeadDesktopLayout({
  selectedClient,
  entityType,
  isLead,
  showExpandedDetails,
  setShowExpandedDetails,
  shouldHideTags,
  leadStagesFromDb,
  clientStateOptions,
  availableUsers,
  notes,
  activities,
  notesLoading,
  t,
  detailsT,
  formatDate,
  handleLeadUpdate,
  handleEditLead,
  onEdit,
  handleCopy,
  handleAddNote,
  handleAddTask,
  handleEditNote,
  handleDeleteNote,
  handleDeleteTask,
  handleEditTask,
  handleUpdateActivities,
  composer,
  onAddPartner,
  showSkeleton,
  partner,
  partnerExtras,
  isModal = false,
  // Header props
  showActionsMenu,
  setShowActionsMenu,
  isConverting,
  onCreateDeal,
  onConvertToClient,
  onClose,
  pipelineContext
}: LeadDesktopLayoutProps) {
  const { hasPermission } = usePermissions();

  // WhatsApp: la chat col lead si apre nel drawer condiviso (stesso di kanban,
  // tabella e cronologia), col numero aziendale scelto dall'utente se ne vede
  // piu' d'uno. Il pannello incorporato di prima usava il numero attivo dello
  // store senza possibilita' di scelta e dal gennaio 2026 andava in errore.
  const { canWhatsApp, openChat: openWhatsAppChat, chooser: waChooser } = useOpenWhatsAppChat();
  const [hasAlfaDocs, setHasAlfaDocs] = useState(false);
  const [rightPanelMode, setRightPanelMode] = useState<'info' | 'alfadocs'>('info');
  const { orgId } = useCurrentUser();


  // Check AlfaDocs: client must have almeno un link AlfaDocs (multi-studio) in metadata
  // AND org must have AlfaDocs active
  const alfaDocsMetadata = useMemo(() => {
    const meta = selectedClient?.metadata as Record<string, any> | null;
    return hasAlfaDocsLink(meta) ? meta : null;
  }, [selectedClient]);

  useEffect(() => {
    if (!orgId) return;
    supabase
      .from('api_configurations')
      .select('id, service_name')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .eq('service_name', 'alfadocs')
      .then(({ data }) => {
        setHasAlfaDocs((data || []).length > 0);
      });
  }, [orgId]);

  const showAlfaDocsTab = hasAlfaDocs && !!alfaDocsMetadata;

  // Load sidebar state from localStorage (default: collapsed)
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(() => {
    const saved = localStorage.getItem('clientSidePanelCollapsed');
    return saved !== null ? saved === 'true' : true;
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('clientSidePanelCollapsed', String(isSidePanelCollapsed));
  }, [isSidePanelCollapsed]);

  return (
    <div className={`hidden lg:flex gap-0 ${isModal ? 'h-full' : ''}`}>
      {/* Left Column - Client Data (1/3) - Notion flat style */}
      <div className={`flex-[1] min-w-0 flex flex-col ${isModal ? 'modal-lead-left-column h-full' : 'bg-white dark:bg-[#1F2937] border-r border-[rgba(55,53,47,0.09)] dark:border-gray-700'}`}>
        {/* Header - only in modal mode */}
        {isModal && showActionsMenu !== undefined && setShowActionsMenu && (
          <div className="px-6 pt-6">
            <LeadDetailsHeader
              selectedClient={selectedClient}
              entityType={entityType}
              isLead={isLead}
              showActionsMenu={showActionsMenu}
              setShowActionsMenu={setShowActionsMenu}
              isConverting={isConverting || false}
              onEditLead={handleEditLead}
              onEdit={onEdit}
              onCreateDeal={onCreateDeal || (() => {})}
              onConvertToClient={onConvertToClient || (() => {})}
              detailsT={detailsT}
              isModal={isModal}
              onClose={onClose}
            />
          </div>
        )}

        {/* Sticky client name/avatar header — con chip fase + assegnatario per
            tenere il contesto durante lo scroll del pannello */}
        {!isModal && (() => {
          const stickyStage = entityType !== 'partner'
            ? leadStagesFromDb.find(
                (s: any) => s.id === ((selectedClient.pipeline_id ? selectedClient.pipeline_stage_id : selectedClient.lead_stage) || ''),
              )
            : null;
          const stickyAssignee = entityType !== 'partner'
            ? availableUsers.find((u: any) => u.id === selectedClient.assigned_to) || selectedClient.assigned_user
            : null;
          return (
            <div className="sticky top-0 z-10 bg-white dark:bg-[#1F2937] border-b border-[rgba(55,53,47,0.06)] dark:border-gray-700/50 px-6 py-3">
              <div className="flex items-center gap-2.5">
                <ClientAvatar client={selectedClient} size="sm" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-[#37352F] dark:text-white truncate">
                    {selectedClient.business_name}
                  </h2>
                  {selectedClient.email && (
                    <p className="text-xs text-[#787774] dark:text-gray-400 truncate">{selectedClient.email}</p>
                  )}
                </div>
                {stickyStage && (
                  <span
                    className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium"
                    style={{ backgroundColor: `${stickyStage.color}20`, color: stickyStage.color }}
                  >
                    {stickyStage.name}
                  </span>
                )}
                {stickyAssignee && (
                  <span
                    className="max-w-[110px] flex-shrink-0 truncate text-[11px] text-[#787774] dark:text-gray-400"
                    title={stickyAssignee.full_name || stickyAssignee.email}
                  >
                    {stickyAssignee.full_name || stickyAssignee.email}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Scrollable content */}
        <div className={`flex-1 overflow-y-auto px-6 pb-6 ${isModal ? 'mt-4' : 'pt-4'}`}>
          {/* Task 4 "Partner drawer unificato": slot fase pipeline al posto di LeadStatusManager per i partner. */}
          {entityType === 'partner' ? (
            selectedClient?.id && (
              <PartnerStageSlot
                partnerId={selectedClient.id}
                onStageChanged={composer?.onSaved}
              />
            )
          ) : (
            <>
              <LeadManagementRows
                lead={selectedClient}
                leadStages={leadStagesFromDb}
                clientStateOptions={clientStateOptions}
                availableUsers={availableUsers}
                onLeadUpdate={handleLeadUpdate}
                canEdit={hasPermission(isLead ? 'leads' : 'clients', 'update')}
                isLead={isLead}
                loading={showSkeleton}
              />
              <LeadAutomazioniInCorso clientId={selectedClient.id} organizationId={selectedClient.organization_id} />
            </>
          )}

          {/* Classificazione sempre visibile: Tag, Settore, Interessi (righe fisse) */}
          {entityType !== 'partner' && (
            <div className="mt-2">
              <LeadClassificationRows
                selectedClient={selectedClient}
                shouldHideTags={shouldHideTags}
                canEdit={hasPermission(isLead ? 'leads' : 'clients', 'update')}
                onLeadUpdate={handleLeadUpdate}
              />
            </div>
          )}

          {/* Informazioni a righe con edit inline; Chiamate AI come riga interna
              (stesso doppio gating org beta + permesso voice_agents di prima) */}
          {entityType !== 'partner' ? (
            <div className="mt-1">
              <LeadInfoRows
                selectedClient={selectedClient}
                canEdit={hasPermission(isLead ? 'leads' : 'clients', 'update')}
                onLeadUpdate={(id, updates) => handleLeadUpdate(id, updates)}
                handleCopy={handleCopy}
                onOpenWhatsApp={canWhatsApp ? (anchor, phone) => openWhatsAppChat(phone, anchor, null, selectedClient?.business_name) : undefined}
                onEditWizard={handleEditLead}
                showVoiceRow={
                  !!selectedClient?.id &&
                  canAccessBetaFeature(BETA_VOICE_AGENTS_ORG_IDS, orgId) &&
                  (hasPermission('voice_agents', 'view') || hasPermission('voice_agents', 'dial'))
                }
                canDialVoice={hasPermission('voice_agents', 'dial')}
                title={detailsT.details.entityInfo}
              />
            </div>
          ) : (
            /* Partner: righe agnostiche pre-redesign (design partner congelato 12/08) */
            <PartnerAgnosticInfoRows
              selectedClient={selectedClient}
              title={detailsT.details.entityInfo}
              handleCopy={handleCopy}
            />
          )}

          {/* Persone (o Aziende, su una scheda persona) + Gerarchia: stanno qui,
              attaccate a Informazioni, perche' chi apre una scheda cerca prima
              "con chi parlo" e poi i commenti (richiesta di Luca, 08/09/2026). */}
          {entityType !== 'partner' && (
            <LeadPeopleSection
              selectedClient={selectedClient}
              canEdit={hasPermission(isLead ? 'leads' : 'clients', 'update')}
              onLeadUpdate={handleLeadUpdate}
            />
          )}

          {/* Task 3 "Partner drawer unificato": property rows partner-specifiche, montate
              subito dopo le property rows agnostiche sopra (email/telefono/indirizzo/P.IVA
              già coperte dal view-model mapPartnerToClientView). */}
          {entityType === 'partner' && partnerExtras && (
            <>
              <Divider />
              <PartnerInfoExtras extras={partnerExtras} />
            </>
          )}

          {/* Commenti — toggle aperto, stesso click-to-edit di prima */}
          <Divider />
          <LeadCommentsSection selectedClient={selectedClient} handleLeadUpdate={handleLeadUpdate} />

          {/* Righe singole Pipeline/Segnalatore/Origine/Privacy + Altri campi
              (toggle) — nessuno di questi concetti si applica a un partner. */}
          {entityType !== 'partner' && (
            <>
              <div className="mt-1">
                <LeadPipelineRow client={selectedClient} />
                <LeadReferrerRow client={selectedClient} />
                <LeadNewsletterRow client={selectedClient} canEdit={hasPermission(isLead ? 'leads' : 'clients', 'update')} onLeadUpdate={handleLeadUpdate} />
                {(selectedClient.ad_platform || selectedClient.utm_source || selectedClient.fbclid || selectedClient.gclid) && (
                  <ClientOriginCard client={selectedClient} />
                )}
                {selectedClient.privacy_consent_given && (
                  <PrivacyConsentCard client={selectedClient} />
                )}
              </div>

              {/* Proposte di enrichment con spunta per campo (sito + VIES, apply esplicito) */}
              {hasPermission(isLead ? 'leads' : 'clients', 'update') && selectedClient?.id && (
                <EnrichmentProposalsSection selectedClient={selectedClient} onLeadUpdate={handleLeadUpdate} />
              )}

              {/* Altri campi: arricchimento (stesso permesso di prima) + campi custom */}
              <PanelSection title="Altri campi">
                {hasPermission('lead_enrichment', 'view') && selectedClient?.id && (
                  <div className="px-1.5">
                    <EnrichmentLeadCard leadId={selectedClient.id} />
                  </div>
                )}
                <div className="px-1.5">
                  <CustomFieldsSection
                    clientId={selectedClient.id}
                    isEditing={false}
                    loading={showSkeleton}
                  />
                </div>
              </PanelSection>
            </>
          )}
        </div>
      </div>

      {/* Center Column - Notes Section (2/5) */}
      <div className={`flex-[2] bg-[#FBFBFA] dark:bg-[#1F2937] p-6 overflow-hidden ${isModal ? 'h-full' : ''}`}>
        <LeadNotesSection
          lead={selectedClient}
          leadNotes={notes}
          leadActivities={activities}
          loadingNotes={notesLoading}
          translations={t}
          formatDate={formatDate}
          onAddNote={handleAddNote}
          onAddTask={handleAddTask}
          onEditNote={handleEditNote}
          onDeleteNote={handleDeleteNote}
          onDeleteActivity={handleDeleteTask}
          onEditActivity={handleEditTask}
          onUpdateActivities={handleUpdateActivities}
          composer={composer}
          loading={showSkeleton}
          isModal={isModal}
          pipelineContext={pipelineContext}
          entityType={entityType === 'partner' ? 'partner' : (isLead ? 'lead' : 'client')}
        />
      </div>

      {/* Right Column - Info / AlfaDocs.
          Task 3 "Partner drawer unificato": per entityType === 'partner' niente toggle
          WhatsApp/AlfaDocs né ClientSidePanel (nessuno dei due concetti si applica a un
          partner) — SOLO tab "Info", stile Notion (PartnerInfoTab, Task 1 "Tab Info
          drawer partner"). Colonna sempre espansa (non collassabile: non c'è
          ClientSidePanel a fornire il bottone di toggle in questo ramo). */}
      <div
        className={`bg-white dark:bg-[#1F2937] border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700 transition-all duration-300 flex flex-col ${isModal ? 'h-full' : ''} ${entityType === 'partner' ? 'flex-1' : isSidePanelCollapsed ? 'w-14' : 'flex-1'}`}
      >
        {entityType === 'partner' ? (
          <div className="flex-1 overflow-y-auto animate-fade-scale-in">
            <div className="flex items-center gap-1.5 px-3.5 pt-3 pb-1 text-xs font-medium text-[#37352F] dark:text-white">
              <Building2 className="w-3.5 h-3.5" />
              Info
            </div>
            {partner ? <PartnerInfoTab partner={partner} /> : null}
          </div>
        ) : (
          <>
        {/* Toggle tabs (solo se AlfaDocs disponibile e pannello non collassato) */}
        {showAlfaDocsTab && (rightPanelMode !== 'info' || !isSidePanelCollapsed) && (
          <div className="flex-shrink-0 flex border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <button
              onClick={() => { setRightPanelMode('info'); setIsSidePanelCollapsed(false); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                rightPanelMode === 'info'
                  ? 'text-[#37352F] dark:text-white border-b-2 border-[#37352F] dark:border-white'
                  : 'text-[#787774] hover:text-[#37352F] dark:hover:text-white'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Info
            </button>
            {showAlfaDocsTab && (
              <button
                onClick={() => { setRightPanelMode('alfadocs'); setIsSidePanelCollapsed(false); }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                  rightPanelMode === 'alfadocs'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                    : 'text-[#787774] hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                AlfaDocs
              </button>
            )}
          </div>
        )}

        {waChooser}
        {rightPanelMode === 'alfadocs' && alfaDocsMetadata ? (
          <PanelErrorBoundary key="alfadocs" label="Impossibile caricare i dati AlfaDocs">
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            }>
              <AlfaDocsClientTab
                clientId={selectedClient.id}
                clientMetadata={alfaDocsMetadata}
                clientName={selectedClient.business_name || ''}
              />
            </Suspense>
          </PanelErrorBoundary>
        ) : (
          <div key="info" className={`flex-1 overflow-y-auto animate-fade-scale-in ${isSidePanelCollapsed ? 'p-2' : 'p-4'}`}>
            <ClientSidePanel
              clientId={selectedClient.id}
              clientName={selectedClient.business_name}
              onCreateDeal={onCreateDeal}
              isCollapsed={isSidePanelCollapsed}
              onToggleCollapse={() => setIsSidePanelCollapsed(!isSidePanelCollapsed)}
              isModal={isModal}
            />
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
